import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { PrivateBookingCard } from "./PrivateBookingCard";
import { DesktopFilter } from "./DesktopFilter";
import { MobileFilterModal } from "./MobileFilterModal";

const GET_PRIVATE_BOOKING_LOCATIONS = gql`
  query PrivateBookingLocations {
    privateBookingLocations {
      nodes {
        name
        slug
        order {
          order
        }
      }
    }
  }
`;

const GET_PRIVATE_BOOKING_TYPES = gql`
  query PrivateBookingTypes {
    bookingTypes {
      nodes {
        name
        slug
        order {
          order
        }
      }
    }
  }
`;

const GET_PRIVATE_BOOKING_PARTY_SIZES = gql`
  query PrivateBookingPartySizes {
    partySizes {
      nodes {
        name
        slug
        order {
          order
        }
      }
    }
  }
`;

const GET_PRIVATE_BOOKING_LIST = gql`
  query PrivateBookingList {
    privateBookings(first: 100) {
      nodes {
        title
        slug
        privateBookingDetail {
          name
          order
          description
          image {
            mediaItemUrl
          }
          bookingUrl
          location {
            name
            slug
          }
          bookingTypeCapacitiesAndAvailability {
            typeOfBooking {
              name
              slug
            }
            numberOfRooms
            typeOfCapacities
            capacities
            capacitiesRange {
              min
              max
            }
            standingCapacities
            connectingRoom
            combined
            combinedRoom
            availability
          }
        }
      }
    }
  }
`;

// Update the sortByOrder helper function
const sortByOrder = (a, b) => {
  // First sort by order
  const orderA = a.order?.order || 0;
  const orderB = b.order?.order || 0;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  // If orders are equal, sort by name alphabetically
  return a.name.localeCompare(b.name);
};

export const PrivateBookingList = () => {
  const [location, setLocation] = useState("all");
  const [partySize, setPartySize] = useState("all");
  const [bookingType, setBookingType] = useState("all");
  const [showFilter, setShowFilter] = useState(false);

  const { data: locationData } = useQuery(GET_PRIVATE_BOOKING_LOCATIONS);
  const { data: partySizeData } = useQuery(GET_PRIVATE_BOOKING_PARTY_SIZES);
  const { data: bookingTypeData } = useQuery(GET_PRIVATE_BOOKING_TYPES);
  const { data: bookingData, loading: bookingLoading } = useQuery(
    GET_PRIVATE_BOOKING_LIST
  );

  const getCapacityRange = (bookingTypes) => {
    if (!bookingTypes || !Array.isArray(bookingTypes)) return null;

    const capacities = bookingTypes
      .map((booking) => {
        if (booking.typeOfCapacities === "range" && booking.capacitiesRange) {
          return [booking.capacitiesRange.min, booking.capacitiesRange.max];
        }
        if (booking.typeOfCapacities === "fixed" && booking.capacities) {
          return booking.capacities;
        }
        return null;
      })
      .flat()
      .filter(Boolean);

    if (!capacities.length) return null;

    const min = Math.min(...capacities);
    const max = Math.max(...capacities);

    return min === max ? min : `${min} - ${max}`;
  };

  const handleClick = (index) => {
    let availabilityHeaders = document.getElementsByClassName(
      "availability-header"
    );
    for (let i = 0; i < availabilityHeaders.length; i++) {
      if (i !== index) {
        availabilityHeaders[i].classList.remove("active");
      } else {
        availabilityHeaders[i].classList.toggle("active");
      }
    }
  };

  const filterBookings = (bookings) => {
    if (!bookings) return [];

    // First filter the bookings
    const filtered = bookings.filter((booking) => {
      const bookingTypes =
        booking.privateBookingDetail.bookingTypeCapacitiesAndAvailability;

      // Location filter
      if (
        location !== "all" &&
        booking.privateBookingDetail.location.slug !== location
      ) {
        return false;
      }

      // Filter booking types based on selected type
      const filteredBookingTypes =
        bookingType !== "all"
          ? bookingTypes.filter(
              (type) => type.typeOfBooking.slug === bookingType
            )
          : bookingTypes;

      // If booking type is filtered and venue doesn't have that type
      if (bookingType !== "all" && filteredBookingTypes.length === 0) {
        return false;
      }

      // Party Size filter
      if (partySize !== "all") {
        const sizeRanges = {
          "11-pax": [0, 11], // < 11 pax
          "11-20": [11, 20],
          "21-30": [21, 30],
          "31-40": [31, 40],
          "41-50": [41, 50],
          50: [50, Infinity],
        };

        const [filterMin, filterMax] = sizeRanges[partySize] || [0, Infinity];

        const hasMatchingCapacity = filteredBookingTypes.some((type) => {
          // Check seating capacity
          let seatingMin, seatingMax;

          if (type.typeOfCapacities === "fixed") {
            seatingMin = seatingMax = type.capacities;
            if (type.connectingRoom && type.numberOfRooms) {
              seatingMax =
                type?.combined || type.capacities * type.numberOfRooms;
            }
          } else if (
            type.typeOfCapacities === "range" &&
            type.capacitiesRange
          ) {
            seatingMin = type.capacitiesRange.min;
            seatingMax = type.capacitiesRange.max;
            if (type.connectingRoom && type.numberOfRooms) {
              seatingMax =
                type?.combined || type.capacitiesRange.max * type.numberOfRooms;
            }
          }

          // Check standing capacity
          const standingCapacity = type.standingCapacities;

          // Collect all valid capacities
          const allCapacities = [];

          if (seatingMin && seatingMax) {
            allCapacities.push({ min: seatingMin, max: seatingMax });
          }

          if (standingCapacity) {
            allCapacities.push({
              min: standingCapacity,
              max: standingCapacity,
            });
          }

          if (!allCapacities.length) return false;

          // Check if any capacity type matches the filter
          return allCapacities.some(({ min, max }) => {
            // Special case for <11 pax
            if (partySize === "11-pax") {
              return min <= filterMax;
            }

            // For infinity range (>50)
            if (filterMax === Infinity) {
              return max >= filterMin;
            }

            // For other ranges (11-20, 21-30, etc)
            return max >= filterMin;
          });
        });

        if (!hasMatchingCapacity) {
          return false;
        }
      }

      return true;
    });

    // Then sort by order and name
    return filtered.sort((a, b) => {
      const orderA = a.privateBookingDetail.order || 9999;
      const orderB = b.privateBookingDetail.order || 9999;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // If orders are equal, sort by name alphabetically
      return a.privateBookingDetail.name.localeCompare(
        b.privateBookingDetail.name
      );
    });
  };

  const filteredBookings = filterBookings(bookingData?.privateBookings?.nodes);

  // Get count of active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (location !== "all") count++;
    if (partySize !== "all") count++;
    if (bookingType !== "all") count++;
    return count;
  };

  const handleApplyFilter = () => {
    setShowFilter(false);
  };

  // Sort the data before rendering
  const sortedLocations = [
    ...(locationData?.privateBookingLocations?.nodes || []),
  ].sort(sortByOrder);
  const sortedPartySizes = [...(partySizeData?.partySizes?.nodes || [])].sort(
    sortByOrder
  );
  const sortedBookingTypes = [
    ...(bookingTypeData?.bookingTypes?.nodes || []),
  ].sort(sortByOrder);
  return (
    <>
      <section className="section-private-booking-filter py-2">
        <div className="container">
          <div className="d-flex d-md-none justify-content-between align-items-center">
            <div className="filter-label">FILTER BY</div>
            <button
              className="btn btn-filter"
              onClick={() => setShowFilter(true)}
            >
              Set Filter{" "}
              {getActiveFilterCount() > 0 && (
                <span className="filter-counter">{getActiveFilterCount()}</span>
              )}
            </button>
          </div>
          <DesktopFilter
            location={location}
            setLocation={setLocation}
            partySize={partySize}
            setPartySize={setPartySize}
            bookingType={bookingType}
            setBookingType={setBookingType}
            sortedLocations={sortedLocations}
            sortedPartySizes={sortedPartySizes}
            sortedBookingTypes={sortedBookingTypes}
          />
        </div>
      </section>

      <MobileFilterModal
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        location={location}
        setLocation={setLocation}
        partySize={partySize}
        setPartySize={setPartySize}
        bookingType={bookingType}
        setBookingType={setBookingType}
        sortedLocations={sortedLocations}
        sortedPartySizes={sortedPartySizes}
        sortedBookingTypes={sortedBookingTypes}
        handleApplyFilter={handleApplyFilter}
      />

      <section className="section-private-booking-list pb-5">
        <div className="container">
          {bookingLoading ? (
            <div className="text-center py-5">
              <p className="mb-0">Loading...</p>
            </div>
          ) : filteredBookings?.length > 0 ? (
            <div className="row">
              {filteredBookings?.map((booking, index) => (
                <div className="col-md-6 col-lg-4" key={booking.slug}>
                  <PrivateBookingCard
                    booking={booking}
                    index={index}
                    onHeaderClick={handleClick}
                    getCapacityRange={getCapacityRange}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="mb-0">Sorry, no results were found.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
